import React, { useEffect, useRef } from 'react';
import { JBButton } from '@jbhi-fi/lanyard-ui';
import { ArrowRight } from '@jbhi-fi/lanyard-ui/components/Icons';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { LoadingSkeleton } from '@jbhi-fi/components/src/LoadingSkeleton/LoadingSkeleton';
import { useFeatureFlag } from '../../hooks/useFeatureFlag/useFeatureFlag';
import { useGoogleSuggestions, useGoogleQuickProducts } from '../../hooks/useGoogleSearch';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { toCloudRetailProductDetail } from '@jbhi-fi/utils/src/ga4Tracking/functions/toCloudRetailProductDetail';
import { SearchProductCardWrapper } from './SearchProductCardWrapper';
import { SearchPlaceholderList } from './SearchPlaceholderList';
import type { PriceRange } from '../../types/search';

const PRODUCTS_LIMIT = 4;

type GoogleQuickResultsProps = {
  searchTerm: string;
  onSearch: (term: string, method: string, priceRange: Partial<PriceRange>, saleItems: boolean) => void;
  validateInput: (e: React.MouseEvent) => void;
  setNbHits: (n: number) => void;
  setNbSaleItems: (n: number) => void;
  setGooglePriceRange: (range: PriceRange) => void;
  priceRange: Partial<PriceRange>;
  isSaleItems: boolean;
};

const GoogleQuickResults = ({
  searchTerm,
  onSearch,
  validateInput,
  setNbHits,
  setNbSaleItems,
  setGooglePriceRange,
  priceRange,
  isSaleItems,
}: GoogleQuickResultsProps) => {
  const isSearchOverlayNewDesign = useFeatureFlag('isSearchOverlayNewDesign');
  const isDesktop = useBreakpoint('md', 'up');
  const prevPriceRangeRef = useRef<{ min: number; max: number } | null>(null);
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;

  const { keywords, isLoading: isSuggestionsLoading } = useGoogleSuggestions(searchTerm);

  // Use the first autocomplete suggestion as the product search query.
  // Only fallback to the raw searchTerm once autocomplete has confirmed no results.
  const isSuggestionsResolved = !isSuggestionsLoading && searchTerm.length >= 2;
  const productQuery = keywords.length > 0 ? keywords[0].query : isSuggestionsResolved ? searchTerm : '';

  // Derive a price filter only when the user has adjusted the range
  const hasUserPriceFilter =
    priceRange && typeof priceRange.min === 'number' && typeof priceRange.max === 'number' && priceRange.max > 0;

  const {
    hits,
    nbHits,
    priceRange: googlePriceRange,
    facets: productFacets,
    attributionToken,
    filter: googleFilter,
    isLoading: isProductsLoading,
    isFetching: isProductsFetching,
  } = useGoogleQuickProducts(productQuery, PRODUCTS_LIMIT, true, {
    priceRange: hasUserPriceFilter ? (priceRange as PriceRange) : undefined,
    isSaleItems,
  });

  const onPromoFacet = productFacets['onPromotion'] ?? productFacets['facets.onPromotion'] ?? {};
  const nbSaleItems = onPromoFacet['true'] ?? onPromoFacet['True'] ?? onPromoFacet['1'] ?? 0;
  useEffect(() => {
    setNbHits(nbHits);
  }, [nbHits, setNbHits]);

  useEffect(() => {
    setNbSaleItems(nbSaleItems);
  }, [nbSaleItems, setNbSaleItems]);

  // Only update slider bounds from the unfiltered response — a price-filtered response
  // would shrink the bounds and trap the slider in the filtered range.
  useEffect(() => {
    if (hasUserPriceFilter) return;
    const prev = prevPriceRangeRef.current;
    if (!prev || prev.min !== googlePriceRange.min || prev.max !== googlePriceRange.max) {
      prevPriceRangeRef.current = googlePriceRange;
      setGooglePriceRange(googlePriceRange);
    }
  }, [googlePriceRange.min, googlePriceRange.max, setGooglePriceRange, hasUserPriceFilter]);

  const productQueryRef = useRef(productQuery);
  productQueryRef.current = productQuery;
  const prevHitsRef = useRef<typeof hits | null>(null);
  useEffect(() => {
    if (isProductsFetching) return;
    if (searchTermRef.current.length < 2) return;
    // Skip when product query hasn't been issued yet (waiting for suggestions)
    if (!productQueryRef.current && hits.length === 0) return;

    const prevHits = prevHitsRef.current;
    const hitsIdentical =
      prevHits !== null &&
      prevHits.length === hits.length &&
      prevHits.every((h, i) => hits[i]?.objectID === h.objectID);
    if (hitsIdentical) return;
    prevHitsRef.current = hits;

    trackViewItemList({
      count: hits.length,
      searchTerm: searchTermRef.current,
      searchType: 'product suggestions',
      currency: getStoreCurrency(),
      listName: 'search - product suggestions',
      viewedItems: transformAlgoliaHitForTracking(hits),
      allowEmptyItems: true,
      ...(hasUserPriceFilter || isSaleItems
        ? {
            resultsFilters: {
              ...(hasUserPriceFilter
                ? {
                    price: {
                      min: String(priceRange.min ?? ''),
                      max: String(priceRange.max ?? ''),
                    },
                  }
                : {}),
              ...(isSaleItems ? { 'Sale items': true } : {}),
            },
          }
        : {}),
      cloudRetail: {
        attributionToken,
        filter: googleFilter ?? null,
        productDetails: hits.map(toCloudRetailProductDetail),
      },
    });
  }, [hits, isProductsFetching, attributionToken, googleFilter, hasUserPriceFilter, priceRange, isSaleItems]);

  const keywordsLimitDesktop = isSearchOverlayNewDesign ? 6 : 10;
  const keywordsLimitMobile = isSearchOverlayNewDesign ? 6 : 5;
  const keywordsLimit = isDesktop ? keywordsLimitDesktop : keywordsLimitMobile;

  const hasKeywords = keywords.length > 0;
  const keywordsReady = isSuggestionsResolved;

  const onSelectKeyword = (e: React.MouseEvent, term: string) => {
    e.preventDefault();
    onSearch(term, 'click suggested keyword', priceRange, isSaleItems);
  };

  const hasProducts = hits.length > 0;
  const isProductsReady = !isProductsLoading && !!productQuery;

  return (
    <div className={`search-quick-results ${isSearchOverlayNewDesign ? '' : 'old'}`}>
      <div className="suggestions">
        <div className="suggested-keywords">
          <CalloutFont as="h3">Suggestions</CalloutFont>
          {hasKeywords ? (
            <ul>
              {keywords.slice(0, keywordsLimit).map((hit, i) => (
                <li key={hit.objectID}>
                  <a href={`/search?query=${hit.query}`} onClick={e => onSelectKeyword(e, hit.query)}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: hit._highlightResult?.query?.value ?? hit.query,
                      }}
                    />
                  </a>
                </li>
              ))}
            </ul>
          ) : keywordsReady && !hasKeywords ? (
            <ul>
              <li>No results</li>
            </ul>
          ) : (
            <SearchPlaceholderList length={keywordsLimit} />
          )}
        </div>
      </div>

      {/* Product suggestions */}
      <div className="product-suggestions">
        <div>
          <JBButton
            label="View all results"
            type="linkWithIcon"
            iconPosition="right"
            icon={<ArrowRight />}
            classes="btn-full-results"
            onClick={validateInput}
          />
        </div>
        <CalloutFont as="h3">Products</CalloutFont>
        <div className="product-suggestions--container">
          {hasProducts ? (
            (() => {
              const productCards = hits
                .slice(0, PRODUCTS_LIMIT)
                .map((hit, index) => (
                  <SearchProductCardWrapper
                    hit={hit}
                    key={hit.objectID}
                    onClick={() => {}}
                    position={index + 1}
                    type="modal"
                    variant="small"
                  />
                ));
              return isDesktop ? (
                <>{productCards}</>
              ) : (
                <Carousel showPagination={false} slidesPerView="auto" fullBleed>
                  {productCards}
                </Carousel>
              );
            })()
          ) : isProductsReady && !hasProducts ? (
            <div>No results</div>
          ) : (
            [...Array(PRODUCTS_LIMIT)].map((_, i) => (
              <LoadingSkeleton.ProductCardSkeleton shouldShimmer variant="small" key={i} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleQuickResults;
